import { StateCreator } from 'zustand'
import { SystemRoles } from '../providers/roles'
import { _Subscriptions } from '../providers/subscriptions'
import axiosInstant, { onRestoreStoredSession, setSessionToken } from '../api'
import { loginEndpoint, myInfo } from '../api/endpoints'
import { USER_INFO, TOKEN, REFRESH_TOKEN, RMN_SLUG } from './constant'
import { clearLocalStorageKeepingLastUsed, rememberSignInMethod } from '../utils/lastSignInMethod'

// The brand slug drives the branded-login return on a 401 (api/index.ts),
// which is read from localStorage because the interceptor has no hooks.
// RmnBrandLayout records it too, but only once a portal page has mounted —
// a client who lands straight on a root personal page (/settings/profile)
// would otherwise hit the generic /login on their first expiry.
const rememberBrandSlug = (info: IUser) => {
  try {
    const brands: any[] = info && Array.isArray(info.brand) ? info.brand : []
    const slug = brands[0] && brands[0].slug ? String(brands[0].slug) : ''
    if (slug) localStorage.setItem(RMN_SLUG, slug)
  } catch (e) {
    // localStorage can throw in private mode; the 401 path falls back to /login.
  }
}

type Roles = keyof typeof SystemRoles
type Subscriptions = keyof typeof _Subscriptions
export interface IUser extends Record<string, unknown> {
  userid: number
  usertype?: string
  isProfileCompleted?: boolean
  brand_team?: Record<string, unknown>[] & { id: number }
  role?: { id: number; name: keyof typeof SystemRoles }
  subscription?: { name: keyof typeof _Subscriptions }
  brand: any
  // /me: per-user RMN allowlists (internal Brand > Team). null when not
  // applicable (internal, brand admin, no network); null arrays = full
  // access on that axis. Cosmetic hiding only — the API enforces.
  rmn_permissions?: {
    rmn_brand_id: number
    brand_ids: number[] | null
    rmn_category_ids: number[] | null
  } | null
  video1: string
  video2: string
  video3: string
  displayName?: string
  msaAgreementSigned?: boolean
}

export interface UserSlice {
  user: IUser | null
  token: string | null
  refreshToken: string | null
  login: (email: string, password: string) => Promise<any>
  autoLogin: (data: any) => Promise<any>
  logout: () => void
  fetchUserInfo: () => Promise<IUser | null>
  setUserInfo: (info: IUser) => void
  updateUserInfo: () => Promise<void>
  setToken: (tokenValue: { refreshToken: string; token: string }) => void
  getUserRole: () => null | Roles
  getUserSubscription: () => null | Subscriptions
  isProfileCompleted: () => boolean
}

export const createUserSlice: StateCreator<UserSlice, [], [], UserSlice> = (set, get) => {
  const user: IUser | null = localStorage.getItem(USER_INFO) ? JSON.parse(localStorage.getItem(USER_INFO) as string) : null
  const token: string | null = localStorage.getItem(TOKEN)
  const refreshToken: string | null = localStorage.getItem(REFRESH_TOKEN)

  /**
   * setToken to localStorage and store
   * @param {string} tokenValue
   * @returns {void}
   */
  const setToken = (tokenValue: { refreshToken: string; token: string }) => {
    set((state) => ({
      token: tokenValue.token,
      refreshToken: tokenValue.refreshToken,
      user: state.user,
    }))
    localStorage.setItem(TOKEN, tokenValue.token)
    localStorage.setItem(REFRESH_TOKEN, tokenValue.refreshToken)
    setSessionToken(tokenValue.token)
  }

  // Called by the API client when the browser dropped the stored token while
  // this tab still holds it (see onRestoreStoredSession in api/index.ts).
  // Writing the user back too keeps a reload signed in. If another tab has
  // since signed in as someone else, only the token is put back, so this
  // tab's user never lands beside a different user's token.
  onRestoreStoredSession((tokenToRestore) => {
    const state = get()
    try {
      localStorage.setItem(TOKEN, tokenToRestore)
      if (state.token !== tokenToRestore) return
      if (state.refreshToken) localStorage.setItem(REFRESH_TOKEN, state.refreshToken)
      if (state.user) localStorage.setItem(USER_INFO, JSON.stringify(state.user))
    } catch (e) {
      // Private mode can refuse storage; requests still use the tab's copy.
    }
  })

  /**
   * fetch user info
   * @returns response
   */
  const fetchUserInfo: () => Promise<IUser | null> = async () => {
    try {
      const res = await axiosInstant.get(myInfo(), {
        headers: {
          Authorization: `Bearer ${get().token}`,
        },
      })
      const { data } = res
      return data ?? null
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   *
   * @param {Object} info
   * @returns void
   */
  const setUserInfo = (info: IUser) => {
    if (info) {
      set(() => ({ user: info }))
      localStorage.setItem(USER_INFO, JSON.stringify(info))
      rememberBrandSlug(info)
    }
  }

  const updateUserInfo = async () => {
    const userInfoRes = await fetchUserInfo()
    if (userInfoRes) setUserInfo(userInfoRes)
  }

  /**
   * login
   * @param {string} email
   * @param {string} password
   * @returns {Promise}
   */
  const login = async (email: string, password: string) => {
    try {
      const res = await axiosInstant.post(loginEndpoint(), {
        emailAddress: email,
        password,
      })
      const { data } = res
      if (data) {
        setToken(data.tokens)
        rememberSignInMethod('password')
        const userInfoRes = await fetchUserInfo()
        if (userInfoRes) setUserInfo(userInfoRes)
      }

      return res
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * Auto Login
   * @param {string} email
   * @param {string} password
   * @returns {Promise}
   */
  const autoLogin = async (data:any) => {
    console.log('autoLogin', data);
    try {
      
      if (data) {
        setToken(data.tokens)
        const userInfoRes = await fetchUserInfo()
        console.log('userInfoRes', userInfoRes);
        if (userInfoRes) setUserInfo(userInfoRes)
      }

      return Promise.resolve(true)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * logout - wipe out localStorage
   * @returns {void}
   */
  const logout = () => {
    set(() => ({ user: null, token: null, refreshToken: null }))
    setSessionToken(null)
    clearLocalStorageKeepingLastUsed()
  }

  const getUserRole = (): Roles | null => {
    const userStoreValue = get().user
    if (!userStoreValue) return null
    // Resolve the role from `usertype` — the reliable field that maps 1:1 to a
    // SystemRoles key. This is the per-role resolution the app has always used,
    // and role-scoped UI (e.g. the notification-settings switches) depends on it.
    // Do NOT prefer userStoreValue.role.name: it is unreliable for non-admin
    // users, which collapsed every non-admin to the same tiny set of switches
    // while admins (resolving to ADMIN) still saw the full list.
    const { usertype: userType } = userStoreValue
    if (userType) {
      const match = Object.entries(SystemRoles).find(([, value]) => value === userType)
      if (match) return match[0] as Roles
    }
    // Fallback only when usertype is missing.
    return userStoreValue.role ? userStoreValue.role.name : null
  }

  const isProfileCompleted: () => boolean = () => {
    const userStoreValue = get().user
    if (!userStoreValue) return false
    return !!userStoreValue.isProfileCompleted
  }

  const getUserSubscription = (): Subscriptions | null => {
    // return null;
    const userStoreValue = get().user
    if (!userStoreValue || !userStoreValue.subscription) return null
    return userStoreValue.subscription.name
    // const { usertype: userType } = userStoreValue
    // const match = Object.entries(SystemRoles).find(([key, value]) => value === userType)

    // return match ? (match[0] as Roles) : null
  }

  return {
    user,
    token,
    refreshToken,
    login,
    autoLogin,
    logout,
    fetchUserInfo,
    setUserInfo,
    updateUserInfo,
    setToken,
    getUserRole,
    getUserSubscription,
    isProfileCompleted,
  }
}
